import { useEffect, useState } from 'preact/hooks';
import { getPlan, planHas, type PlanApi } from '../lib/api';
import { useRecordsVersion } from '../lib/recordEditor';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { dayLabel, loadCalendarItems, type CalendarItem } from '../lib/calendarItems';
import './spread.css';
import './householdCalendar.css';

// A-SERIES — THE CALENDAR, INSIDE THE APP.
//
// Holds were written for a year of outside dates, callbacks and shoot days
// and the only place they could be seen was a calendar app the household had
// subscribed a feed to. `#/calendar` bounced to Today, the rail had no tab,
// and `Calendar.tsx` reads the cockpit's vocabulary. So the feature was
// built, green and invisible — the seventh time that shape shipped.
//
// ===================================================================
// WHY THIS IS NOT `Calendar.tsx` WITH A BRANCH IN IT
// ===================================================================
// The cockpit's agenda is a list of EVENTS somebody sent Huan. A household's
// week is a list of RECORDS the family already owns — an audition, a session,
// a called day — each of which has a screen of its own. So the row here is a
// DOOR, and that is the whole difference: a calendar you can only look at is
// a calendar you check somewhere else instead.
//
// ===================================================================
// A HOLD IS RENDERED WITH THE WORD, AND THE WORD IS "HELD"
// ===================================================================
// Core gap 18's first law: A HOLD REFUSES NOTHING. No write path consults
// overlap, so a hold MARKS a day and never blocks one — and the copy has to
// say so, because a family reading "blocked" would plan around a promise the
// product never made. The word rides every hold row (never a colour, never a
// stripe on its own), and the one sentence at the top of the screen says what
// the word means, once.
//
// ===================================================================
// TIER HONESTY, RESOLVED THE WAY THE RULING RESOLVES IT
// ===================================================================
// `calendar.holds` is Working (the plan model's own table). The register left
// open what Free should then see here. The ruling — every paid feature has a
// surface to explore in the app — settles it in the only direction that is
// not a lie: FREE SEES ITS OWN DATES. Sessions, called days and due dates are
// the family's records on every tier, and withholding the screen that shows
// them would be gating retention rather than the feature. What Working adds
// is the marking, and where a hold would be there is one honest line saying
// so instead.

export interface HouseholdCalendarProps {
  tenantId: string;
  onOpenAudition: (id: string) => void;
  onOpenShootDay: (id: string) => void;
}

/** How far ahead the agenda reads. The same 60 days the cockpit's agenda
 * takes — long enough that a block of outside dates two months out is on the
 * screen the day it is read in, short enough that the list is a week's work
 * rather than a year's. */
const WINDOW_DAYS = 60;

// B4 (2026-09-24): the row shape, and everything that assembles one, moved
// to `lib/calendarItems.ts` (`CalendarItem`, `loadCalendarItems`) so Today's
// "Coming up" section reads the identical items rather than a second,
// drifting copy. This screen's own behaviour is unchanged — same window,
// same reads, same rows — it now just asks the shared function for them.

export function HouseholdCalendar({
  tenantId,
  onOpenAudition,
  onOpenShootDay,
}: HouseholdCalendarProps) {
  const [entries, setEntries] = useState<CalendarItem[] | null>(null);
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [plan, setPlan] = useState<PlanApi | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const recordsVersion = useRecordsVersion();

  // ⚠ A HINT, NEVER A GATE (lib/api's own header on planHas). The tier decides
  // one sentence on this screen and nothing else; the holds themselves are
  // gated where they are WRITTEN (the pack's three hold reactions). A failed
  // plan read leaves `plan` null and `planHas` answers false, which shows the
  // line — the safe direction, since the sentence is true of Free.
  useEffect(() => {
    let cancelled = false;
    void getPlan(tenantId)
      .then((p) => {
        if (!cancelled) setPlan(p);
      })
      .catch(() => {
        // The agenda is complete without it. See above for why false is safe.
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, retryNonce]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setFailure(null);
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const to = new Date(from.getTime() + WINDOW_DAYS * 86_400_000);
      try {
        const out = await loadCalendarItems(tenantId, from, to, { onOpenAudition, onOpenShootDay });
        if (cancelled) return;
        setEntries(out);
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setEntries(null);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, retryNonce, recordsVersion]);

  if (failure) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">Calendar</div>
        <div class="sp__say">
          {failure.headline}
          <br />
          <span class="sp__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="sp__retry" onClick={() => setRetryNonce((n) => n + 1)}>
            Read the calendar again
          </button>
        ) : null}
      </div>
    );
  }

  if (entries === null) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">Calendar</div>
        <div class="sp__say">One moment…</div>
      </div>
    );
  }

  const holdsOn = planHas(plan, 'calendar.holds');
  const days = [...new Set(entries.map((e) => e.day))];

  return (
    <div class="sp">
      <div class="sp__eyebrow">Calendar</div>

      {/* THE ONE SENTENCE THAT SAYS WHAT THE WORD MEANS — once, at the top,
          not repeated on every row. On Free the same slot carries the honest
          line about what Working adds instead. Neither is an instruction. */}
      {holdsOn ? (
        <p class="cal__law" data-testid="holds-law">
          A hold marks a day. It refuses nothing — the day stays yours to fill.
        </p>
      ) : (
        /* ⚑ WHAT WORKING ACTUALLY ADDS (Day 2 walk, S4.11). This slot used to
           read "On Working, outside dates, callbacks and shoot days also mark
           this calendar" — and then the screen listed a callback and a shoot
           day directly under it, on Free. Both of those are DATED RECORDS and
           are drawn on every tier; the teaser was selling back two things the
           family could already see, and never named the one thing the rung
           buys. What holds add is the MARKING — the word held on a day a job
           may need you — and one row that exists nowhere else: a role's
           outside dates, which are retained on every tier and have no record
           of their own to sit on. */
        <p class="sp__craft-say" data-testid="holds-teaser">
          On Working, the days a job may need you are marked held — including a role&rsquo;s outside dates,
          which have no other row.
        </p>
      )}

      {entries.length === 0 ? (
        <div class="sp__empty">
          Nothing dated in the next {WINDOW_DAYS} days. Sessions, called days and the dates a tape
          is owed by land here as they are read from what the household sends in.
        </div>
      ) : (
        days.map((day) => (
          <section class="cal__day" key={day}>
            <h2 class="cal__day-title">{dayLabel(day)}</h2>
            {entries
              .filter((e) => e.day === day)
              .map((entry) => {
                const inside = (
                  <>
                    <span class={`cal__when${entry.isHold ? ' cal__when--held' : ''}`}>
                      {entry.when}
                      {/* THE MARK. A row whose day is also held wears the word
                          under its time — one row, both facts, and the word is
                          the signal rather than the colour beside it. */}
                      {entry.marked ? <span class="cal__mark">held</span> : null}
                    </span>
                    <span class="cal__main">
                      <span class="cal__what">{entry.what}</span>
                      <span class="cal__sub">{entry.sub}</span>
                    </span>
                  </>
                );
                // A row with nowhere to go is not a dead handle — it renders
                // as the statement it is.
                return entry.open ? (
                  <button type="button" class="cal__row" key={entry.id} onClick={entry.open}>
                    {inside}
                  </button>
                ) : (
                  <div class="cal__row cal__row--flat" key={entry.id}>
                    {inside}
                  </div>
                );
              })}
          </section>
        ))
      )}
    </div>
  );
}
